
import Vue from 'vue';
import { VueGoodTable } from 'vue-good-table';
import 'vue-good-table/dist/vue-good-table.css';
import { Component, Inject, Prop, Watch } from 'vue-property-decorator';
import Toasted from 'vue-toasted';
import { exposeHeaders, handleError, initClient } from '../../../api';
import { blobDownload, formatDate, WindowType } from '../../../util';
import { ArchiveRequest, MeasurementSpecification, TestEquipmentDevice, TestEquipmentApiUiV2Client, TestEquipmentTest } from '../../backend/backendapi';
import ArchiveModal from '../archivemodal/ArchiveModal';
import { RiskOption } from '../archivemodal/archivemodal.model';
import TestEquipmentEditModal from '../edit/TestEquipmentEditModal';
import TestEquipmentTestEditModal from '../test/TestEquipmentTestEditModal';
import TeImportModal from './TeImportModal';
import { paginationOptions, searchOptions } from '../../../util/vue-good-table';
import State from '../State';
import { Results, VgtParameters, VgtRemoteManger } from '../../../util/VgtRemoteManger';
import MeasurementSpecificationsTemplateBadges from '../measurementSpecificationTemplates/MeasurementSpecificationsTemplateBadges';

declare var window: WindowType;

Vue.use(Toasted, { duration: 2000 });

@Component({
  components: {
    ArchiveModal,
    MeasurementSpecificationsTemplateBadges,
    TestEquipmentEditModal,
    TestEquipmentTestEditModal,
    TeImportModal,
    VueGoodTable,
  },
})
export default class TestEquipmentList extends Vue {
  @Prop({ default: '' })
  readonly filter!: string;
  @Prop({ default: true })
  readonly editable!: boolean;
  @Prop({ default: false })
  readonly showArchiveFields!: boolean;
  @Prop({ default: false })
  readonly showOpenDays!: boolean;


  @Inject({ from: 'state' })
  readonly state!: State;

  //runtime
  newEquipmentFiles: string[] = [];
  vgtManager = new VgtRemoteManger<TestEquipmentDevice>(this.loadDevices.bind(this), 'en', [
    { field: 'combinedIdentifier', type: 'string' },
    { field: 'deviceManufacturer', type: 'string' },
    { field: 'lastTester', type: 'string' },
    { field: 'lastTestDate', type: 'date' },
    { field: 'name', type: 'string' },
    { field: 'nextTestDate', type: 'date' },
    { field: 'placeOfUse', type: 'string' },
    { field: 'responsible', type: 'string' },
    { field: 'status', type: 'string' },
    { field: 'ident', type: 'string' },
    { field: 'costCenter', type: 'string' },
    { field: 'deviceSerialNumber', type: 'string' },
    { field: 'testProcedure', type: 'string' },
  ]);

  //api
  private readonly equipmentAPI = exposeHeaders(initClient(TestEquipmentApiUiV2Client), 'listDevices');

  //config
  selectConfig = {};

  get canUpload(): boolean {
    return window.application.permissions.testequipment.edit;
  };

  get columns(): { label: string, field: unknown }[] {
    const ret: { label: string, field: unknown, [key: string]: unknown }[] = [
      {
        label: this.$t('testEquipment.list.title').toString(),
        field: 'combinedIdentifier',
      },
      {
        label: this.$t('testEquipment.list.name').toString(),
        field: 'name',
      },
      {
        label: this.$t('testEquipment.list.lastTester').toString(),
        field: 'lastTester',
      },
      {
        label: this.$t('testEquipment.list.lastTestDate').toString(),
        field: 'lastTestDate',
        type: 'date',
        formatFn: (lastTestDate: Date | undefined) => lastTestDate ? formatDate(lastTestDate) : '',
      },
      {
        label: this.$t('testEquipment.list.intervalValue').toString(),
        field: (row: TestEquipmentDevice) => row.intervalValue + ' ' + (this.state.timeUnitsStore.timeUnitsById.get(row.intervalUnitId!)?.name ?? ''),
        sortable: false,
      },
      {
        label: this.$t('testEquipment.list.nextTestDate').toString(),
        field: 'nextTestDate',
        type: 'date',
        formatFn: (nextTestDate: Date | undefined) => nextTestDate ? formatDate(nextTestDate) : '',
      },
      {
        label: this.$t('testEquipment.list.responsible').toString(),
        field: 'responsible',
      },
      {
        label: this.$t('testEquipment.list.placeOfUse').toString(),
        field: 'placeOfUse',
      },
      {
        label: this.$t('testEquipment.list.deviceManufacturer').toString(),
        field: 'deviceManufacturer',
      },
      {
        label: this.$t('testEquipment.list.measurementSpecificationTemplates').toString(),
        field: 'measurementSpecifications',
        sortable: false,
      },
    ];

    if (this.showOpenDays) {
      ret.push({
        label: this.$t('testEquipment.list.daysOpen').toString(),
        field: 'daysOpen',
        sortable: true,
      });
    }

    if (this.showArchiveFields) {
      ret.push(...[
        {
          label: this.$t('testEquipment.list.archiveReason').toString(),
          field: 'archiveReason',
        },
        {
          label: this.$t('testEquipment.list.archiveRisk').toString(),
          field: (row: TestEquipmentDevice) => {
            let localRiskOptions = this.state.riskOptionsSetting[this.$i18n.locale] as RiskOption[];
            localRiskOptions ??= this.state.riskOptionsSetting['en'] as RiskOption[];
            if (!localRiskOptions) {
              return row.archiveRisk!;
            }
            return localRiskOptions.find(e => e.id === row.archiveRisk!)?.name ?? row.archiveRisk!;
          },
        },
      ]);
    }

    if (this.editable) {
      ret.push(...[{
        label: this.$t('testEquipment.list.actions').toString(),
        field: 'actions',
      }]);
    }

    return ret;
  }

  get paginationOptions(): any {
    return paginationOptions(this.$i18n);
  }

  get searchOptions(): any {
    return searchOptions(this.$i18n);
  }

  get windowArchive(): boolean {
    return window.application.permissions.testequipment.archive;
  }

  get windowAudit(): boolean {
    return window.application.permissions.testequipment.audit;
  }

  get windowEdit(): boolean {
    return window.application.permissions.testequipment.edit;
  }

  mounted(): void {
    //only load if not already loaded by onFilterChanged()
    if (!this.vgtManager.results.data) {
      this.vgtManager.reload();
    }
  }

  createNew(): void {
    const equipment = TestEquipmentDevice.fromJS({
      id: 0,
      issueDate: new Date(),
      entryDate: new Date(),
      itemNo: 'xxx',
      files: [],
      measurementSpecifications: [],
    });
    this.newEquipmentFiles.splice(0);
    (this.$refs.equipmentModal as TestEquipmentEditModal).open(equipment, []);
  }

  async loadDevices(params: VgtParameters): Promise<Results<TestEquipmentDevice>> {
    const response = await this.equipmentAPI.listDevices(params.filter, params.sort, params.page, params.pageSize);
    return { data: response.content, totalRows: parseInt(response.headers.get('total') ?? '0') };
  }

  async save(equipmentFinal: TestEquipmentDevice): Promise<void> {
    if (equipmentFinal.id < 1) {
      let equipmentFromServer: TestEquipmentDevice;
      try {
        const res = await this.equipmentAPI.createDevices([equipmentFinal]);
        this.$toasted.info(this.$t('txtSaved').toString());
        await this.vgtManager.reload();
        (this.$refs.equipmentModal as TestEquipmentEditModal).dismiss();
        equipmentFromServer = res[0];
      } catch (err) {
        handleError(this.$toasted, this.$i18n, err);
        return;
      }

      //link files
      for (let i = 0; i < this.newEquipmentFiles.length; i++) {
        await this.equipmentAPI.linkDeviceMedia(equipmentFromServer.id, this.newEquipmentFiles[i])
            .then(r => {
              equipmentFinal.files.push(r!);
            })
            .catch(err => handleError(this.$toasted, this.$i18n, err));
      }
    } else {
      await this.equipmentAPI.updateDevice(equipmentFinal.id, equipmentFinal)
          .then(r => {
            this.$toasted.info(this.$t('txtSaved').toString());
            this.vgtManager.reload();
            (this.$refs.equipmentModal as TestEquipmentEditModal).dismiss();
          })
          .catch(err => handleError(this.$toasted, this.$i18n, err));
    }
  }

  async equipmentFileAdded(event: { fileId: string, equipment: TestEquipmentDevice }): Promise<void> {
    console.log('equipmentFileAdded', event.fileId);
    if (!event.equipment.id) {
      this.newEquipmentFiles.push(event.fileId); //update media in save/create
    } else {
      await this.equipmentAPI.linkDeviceMedia(event.equipment.id, event.fileId)
          .then(r => {
            this.$toasted.info(this.$t('txtSaved').toString());
            event.equipment.files.push(r!);
          })
          .catch(err => handleError(this.$toasted, this.$i18n, err));
    }
  }

  async onClick(arg: { row: TestEquipmentDevice, pageIndex: number, selected: boolean, event: MouseEvent }): Promise<void> {
    this.newEquipmentFiles.splice(0);
    let tests: TestEquipmentTest[] | null = null;
    try {
      tests = await this.equipmentAPI.listTests(arg.row.id);
    } catch (err) {
      handleError(this.$toasted, this.$i18n, err);
    }
    (this.$refs.equipmentModal as TestEquipmentEditModal).open(TestEquipmentDevice.fromJS(arg.row), tests);
  }

  onArchive(row: TestEquipmentDevice): void {
    (this.$refs.archivemodal as ArchiveModal).open(row);
  }

  onArchiveConfirm(model: ArchiveRequest): void {
    this.equipmentAPI.archiveDevice(model.id, model)
        .then(r => {
          (this.$refs.archivemodal as ArchiveModal).dismiss();
          this.vgtManager.reload();
          this.$toasted.info(this.$t('txtSaved').toString());
        })
        .catch(err => handleError(this.$toasted, this.$i18n, err));
  }

  onAddTest(row: TestEquipmentDevice): void {
    const test = TestEquipmentTest.fromJS({
      id: 0,
      testEquipmentId: row.id,
      testDate: new Date(),
      testSpecification: row.testSpecification,
      expectedError: row.errorMargin,
      files: [],
      measurementSpecifications: row.measurementSpecifications?.map(e => MeasurementSpecification.fromJS(e)) ?? [],
      measurements: [],
    });
    (this.$refs.testModal as TestEquipmentTestEditModal).open(row, test);
  }

  genCSV(): string {
    const table = this.$refs.table as VueGoodTable;

    const cols = this.columns.filter(e => e.field !== 'actions');
    const csv = '\ufeff' //utf8 bom (byte order marker)
        + cols.map(e => e.label).join(';') + '\n'
        + ((table.paginated?.length ?? 0) === 0 ? '' : table.paginated[0].children.map(row => {
          return cols.map(col => {
            if (typeof col.field === 'string') {
              return ('' + (row[col.field] ?? '')).split(';').join(',');
            } else if (typeof col.field === 'function') {
              return ('' + (col.field(row) ?? '')).split(';').join(',');
            } else {
              return 'cannot extract value';
            }
          }).join(';');
        }).join('\n'));

    console.log(csv);
    return csv;
  }

  exportCSV(): void {
    const data = this.genCSV();
    const fileName = 'export_equipment.csv';

    const blob = new Blob([data], { type: 'octet/stream' });
    blobDownload(blob, fileName);
  }


  openImportModal(): void {
    (this.$refs.importModal as TeImportModal).open();
  }

  @Watch('filter', { immediate: true })
  onFilterChanged(newValue: string, oldValue: string): void {
    if (newValue === oldValue) {
      return;
    }
    this.vgtManager.extraFilter = this.filter;
  }

  @Watch('$i18n.locale', { immediate: true })
  onLocaleChanged(newValue: string, oldValue: string): void {
    if (newValue === oldValue) {
      return;
    }
    this.vgtManager.dateFormat = this.$t('dates.dateFormat.momentJs').toString();
  }
}

